import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Formik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import Button from '../../BaseComponents/Button';
import MultiSelect from '../../BaseComponents/MultiSelect';
import { SelectOption } from '../../BaseComponents/Select';
import UploadPhoto from '../../UploadPhoto';
import {
  createResource,
  updateResource,
  Resource,
  CreateResourceRequest,
  UpdateResourceRequest,
  getResourceBodySystemLabel,
  getResourceMriConditionLabel,
} from '../../../api/resourceCenter';
import { getBiomarkersList } from '../../../api/biomarkers';
import { getConditionsList } from '../../../api/conditions';
import { getBodySystemsList } from '../../../api/body-systems';
import { getMriConditionsList } from '../../../api/mriRadiology';
import { putStatic } from '../../../api/fileStorage';
import { getFullImageUrl } from '../../../utils/constants';
import trash from "../../../assets/icons/TrashBin2.svg";
import 'trix/dist/trix.css';
import 'trix';
import './ResourceFormModal.scss';

interface ResourceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceToEdit?: Resource | null;
  onResourceSaved: () => void;
  onDeleteResource?: (resourceId: number) => void;
}

interface FormValues {
  name: string;
  biomarkers: SelectOption[];
  conditions: SelectOption[];
  bodySystems: SelectOption[];
  mriConditions: SelectOption[];
  articleText: string;
  videoUrl: string;
  image: string;
}

const parseSelectedIds = (options: SelectOption[]): number[] =>
  options
    .filter((option) => option && option.key && option.key.trim() !== '')
    .map((option) => parseInt(option.key.trim(), 10))
    .filter((num): num is number => !isNaN(num));

const isValidURL = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const toggleMultiSelectOption = (
  currentOptions: SelectOption[],
  option: SelectOption,
  setFieldValue: (field: string, value: SelectOption[]) => void,
  fieldName: string
) => {
  const existingIndex = currentOptions.findIndex((item) => item && item.key === option.key);
  if (existingIndex >= 0) {
    setFieldValue(fieldName, currentOptions.filter((_, index) => index !== existingIndex));
  } else {
    setFieldValue(fieldName, [...currentOptions, option]);
  }
};

const CustomTrixEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder, className }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const inputId = useMemo(() => `trix-input-${Math.random().toString(36).substr(2, 9)}`, []);

  onChangeRef.current = onChange;
  valueRef.current = value;

  useEffect(() => {
    const input = inputRef.current;
    const editor = editorRef.current;
    if (!input || !editor) return;

    const trixEditor = document.createElement('trix-editor');
    trixEditor.setAttribute('input', inputId);
    if (placeholder) trixEditor.setAttribute('placeholder', placeholder);

    editor.innerHTML = '';
    editor.appendChild(trixEditor);

    input.value = valueRef.current || '';
    (trixEditor as any).editor.loadHTML(valueRef.current || '');

    const handleFileAccept = async (event: any) => {
      const file = event.file;
      if (!file || !file.type.startsWith('image/')) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      try {
        const editorInstance = (trixEditor as any).editor;
        const uploadResponse = await putStatic({ file });
        
        if (!uploadResponse || !uploadResponse.full_key) {
          alert('Failed to upload image. Please try again.');
          return;
        }

        const fullImageUrl = getFullImageUrl(uploadResponse.full_key);
        const imgHtml = `<img src="${fullImageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;">`;
        editorInstance.insertHTML(imgHtml);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };

    const handleChange = () => {
      onChangeRef.current(trixEditor.innerHTML);
    };
    
    trixEditor.addEventListener('trix-file-accept', handleFileAccept);
    trixEditor.addEventListener('trix-change', handleChange);

    return () => {
      trixEditor.removeEventListener('trix-file-accept', handleFileAccept);
      trixEditor.removeEventListener('trix-change', handleChange);
    };
  }, [inputId, placeholder]);

  return (
    <div className={className}>
      <input ref={inputRef} id={inputId} type="hidden" value={value} />
      <div ref={editorRef} />
    </div>
  );
};

const ResourceFormModal: React.FC<ResourceFormModalProps> = ({
  isOpen,
  onClose,
  resourceToEdit,
  onResourceSaved,
  onDeleteResource
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [biomarkerOptions, setBiomarkerOptions] = useState<SelectOption[]>([]);
  const [conditionOptions, setConditionOptions] = useState<SelectOption[]>([]);
  const [bodySystemOptions, setBodySystemOptions] = useState<SelectOption[]>([]);
  const [mriConditionOptions, setMriConditionOptions] = useState<SelectOption[]>([]);

  const getInitialValues = useCallback((): FormValues => {
    if (resourceToEdit) {
      return {
        name: resourceToEdit.name || '',
        biomarkers: (resourceToEdit.biomarkers || []).map((biomarker) => ({
          key: biomarker.id.toString(),
          value: biomarker.name || `Biomarker ${biomarker.id}`
        })),
        conditions: (resourceToEdit.conditions || []).map((condition) => ({
          key: condition.id.toString(),
          value: condition.name || `Condition ${condition.id}`
        })),
        bodySystems: (resourceToEdit.body_systems || []).map((bodySystem) => ({
          key: bodySystem.id.toString(),
          value: getResourceBodySystemLabel(bodySystem)
        })),
        mriConditions: (resourceToEdit.mri_conditions || []).map((mriCondition) => ({
          key: mriCondition.id.toString(),
          value: getResourceMriConditionLabel(mriCondition)
        })),
        articleText: resourceToEdit.content || '',
        videoUrl: resourceToEdit.video_url || '',
        image: resourceToEdit.image || '',
      };
    }

    return {
      name: '',
      biomarkers: [],
      conditions: [],
      bodySystems: [],
      mriConditions: [],
      articleText: '',
      videoUrl: '',
      image: '',
    };
  }, [resourceToEdit]);

  const initialValues = useMemo(
    () => getInitialValues(),
    [getInitialValues]
  );

  useEffect(() => {
    if (isOpen) {
      loadOptions();
    }
  }, [isOpen]);

  const loadOptions = async () => {
    try {
      const [biomarkersResponse, conditionsResponse, bodySystemsResponse, mriConditionsResponse] =
        await Promise.all([
          getBiomarkersList(),
          getConditionsList(undefined, 200, 0),
          getBodySystemsList(),
          getMriConditionsList(200, 0),
        ]);

      setBiomarkerOptions(
        (biomarkersResponse.biomarkers || []).map((bm) => ({
          key: bm.id.toString(),
          value: bm.name
        }))
      );
      setConditionOptions(
        (conditionsResponse.conditions || []).map((cond) => ({
          key: cond.id.toString(),
          value: cond.name
        }))
      );
      setBodySystemOptions(
        (bodySystemsResponse.body_systems || []).map((bodySystem) => ({
          key: bodySystem.id.toString(),
          value: bodySystem.title
        }))
      );
      setMriConditionOptions(
        (mriConditionsResponse.items || []).map((mriCondition) => ({
          key: mriCondition.id.toString(),
          value: mriCondition.condition_description
        }))
      );
    } catch (error) {
      console.error('Error loading content association options:', error);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsLoading(true);

      const biomarkerIds = parseSelectedIds(values.biomarkers);
      const conditionIds = parseSelectedIds(values.conditions);
      const bodySystemIds = parseSelectedIds(values.bodySystems);
      const mriConditionIds = parseSelectedIds(values.mriConditions);
      const videoUrl = values.videoUrl.trim();

      if (resourceToEdit) {
        const updateData: UpdateResourceRequest = {
          resource_id: parseInt(resourceToEdit.id),
          name: values.name,
          content: values.articleText,
          biomarker_ids: biomarkerIds,
          condition_ids: conditionIds,
          body_system_ids: bodySystemIds,
          mri_condition_ids: mriConditionIds,
          image: values.image,
          video_url: videoUrl,
        };
        await updateResource(updateData);
      } else {
        const createData: CreateResourceRequest = {
          name: values.name,
          content: values.articleText,
          biomarker_ids: biomarkerIds,
          condition_ids: conditionIds,
          body_system_ids: bodySystemIds,
          mri_condition_ids: mriConditionIds,
          image: values.image,
          video_url: videoUrl,
        };

        await createResource(createData);
      }
      
      onResourceSaved();
    } catch (error) {
      console.error('Error saving content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (resourceToEdit && onDeleteResource) {
      try {
        setIsLoading(true);
        await onDeleteResource(parseInt(resourceToEdit.id));
        onResourceSaved();
      } catch (error) {
        console.error('Error deleting content:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="800px">
      <div className="resource-modal-container">
        <div className="modal-header">
          <h2 className="modal-title">
            {resourceToEdit ? 'Edit Content' : 'Add Content'}
          </h2>
        </div>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
          validate={(values) => {
            const errors: Partial<Record<keyof FormValues, string>> = {};
            
            if (!values.name || values.name.trim() === '') {
              errors.name = 'Name is required';
            }

            if (values.videoUrl?.trim() && !isValidURL(values.videoUrl.trim())) {
              errors.videoUrl = 'Please enter a valid URL';
            }
            
            return errors;
          }}
        >
          {({ handleSubmit, values, setFieldValue, errors, touched }) => (
            <form onSubmit={handleSubmit} className="modal-content">
              <div className="form-section">
                <div className="section-title">Image</div>
                <div className="upload-photo-wrapper">
                  <UploadPhoto
                    defaultPhoto={resourceToEdit?.image}
                    onPhotoChange={(photo) => setFieldValue('image', photo)}
                    putToStaticFolder={true}
                  />
                </div>
              </div>

              <div className="form-section">
                <div className="section-title">Name <span style={{color: 'red'}}>*</span></div>
                <TextInput
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue('name', e.target.value)}
                  placeholder="Enter name of the content"
                  isError={!!(errors.name && touched.name)}
                />
                {errors.name && touched.name && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {errors.name}
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3>Choose blood biomarkers (optional)</h3>
                <div className="form-row">
                  {biomarkerOptions.length > 0 ? (
                    <MultiSelect
                      name="biomarkers"
                      options={biomarkerOptions}
                      selectedOptions={values.biomarkers || []}
                      onChange={(option) =>
                        toggleMultiSelectOption(
                          values.biomarkers || [],
                          option,
                          setFieldValue,
                          'biomarkers'
                        )
                      }
                      placeholder="Choose blood biomarkers (click to add/remove)"
                      isError={false}
                    />
                  ) : (
                    <div className="biomarkers-error">
                      No blood biomarkers available
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h3>Choose conditions (optional)</h3>
                <div className="form-row">
                  {conditionOptions.length > 0 ? (
                    <MultiSelect
                      name="conditions"
                      options={conditionOptions}
                      selectedOptions={values.conditions || []}
                      onChange={(option) =>
                        toggleMultiSelectOption(
                          values.conditions || [],
                          option,
                          setFieldValue,
                          'conditions'
                        )
                      }
                      placeholder="Choose conditions (click to add/remove)"
                      isError={false}
                    />
                  ) : (
                    <div className="conditions-error">
                      No conditions available
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h3>Choose body systems (optional)</h3>
                <div className="form-row">
                  {bodySystemOptions.length > 0 ? (
                    <MultiSelect
                      name="bodySystems"
                      options={bodySystemOptions}
                      selectedOptions={values.bodySystems || []}
                      onChange={(option) =>
                        toggleMultiSelectOption(
                          values.bodySystems || [],
                          option,
                          setFieldValue,
                          'bodySystems'
                        )
                      }
                      placeholder="Choose body systems (click to add/remove)"
                      isError={false}
                    />
                  ) : (
                    <div className="conditions-error">
                      No body systems available
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h3>Choose MRI conditions (optional)</h3>
                <div className="form-row">
                  {mriConditionOptions.length > 0 ? (
                    <MultiSelect
                      name="mriConditions"
                      options={mriConditionOptions}
                      selectedOptions={values.mriConditions || []}
                      onChange={(option) =>
                        toggleMultiSelectOption(
                          values.mriConditions || [],
                          option,
                          setFieldValue,
                          'mriConditions'
                        )
                      }
                      placeholder="Choose MRI conditions (click to add/remove)"
                      isError={false}
                    />
                  ) : (
                    <div className="conditions-error">
                      No MRI conditions available
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <div className="section-title">Article Text (optional)</div>
                <div className="trix-editor-wrapper">
                  <CustomTrixEditor
                    value={values.articleText}
                    onChange={(html) => setFieldValue('articleText', html)}
                    placeholder="Enter article text..."
                    className="trix-editor"
                  />
                </div>
              </div>

              <div className="form-section">
                <div className="section-title">Video link (optional)</div>
                <TextInput
                  name="videoUrl"
                  value={values.videoUrl}
                  onChange={(e) => setFieldValue('videoUrl', e.target.value)}
                  placeholder="Enter video URL"
                  isError={!!(errors.videoUrl && touched.videoUrl)}
                />
                {errors.videoUrl && touched.videoUrl && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {errors.videoUrl}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <Button
                  text={resourceToEdit ? 'Save' : 'Add Content'}
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  isDisabled={
                    isLoading ||
                    !values.name ||
                    values.name.trim() === '' ||
                    !!(values.videoUrl?.trim() && !isValidURL(values.videoUrl.trim()))
                  }
                />
                {resourceToEdit && onDeleteResource && (
                  <button 
                    className="delete-button"
                    onClick={handleDelete}
                    disabled={isLoading}
                  >
                    <img src={trash} alt="delete" />
                    Delete
                  </button>
                )}
              </div>
            </form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default ResourceFormModal;
